import { AnyActorRef } from 'xstate';
import { z } from 'zod';
import { ERROR_MESSAGES } from '~/constants';
import { AppActorSystem } from '~/features/xstateSystem';
import { AreaOfInterest } from '~/types';
import { optionSchemaRequired, OptionType } from '../../types';
import { Events } from './events';

export const aoiOptionSchema = z.object({
  value: z.string().readonly(),
  label: z.string().readonly(),
  category: z.string().readonly(),
  description: z.string().readonly(),
  specialtyName: z.string().readonly(),
});
export type AoiOptionType = z.infer<typeof aoiOptionSchema>;

// [MCV-873] Require a non-empty `value` so "Add" on an unpicked form fails
// with an inline field-level message instead of a vague server-side error.
const aoiOptionSchemaRequired = aoiOptionSchema.extend({
  value: z.string().min(1, { message: ERROR_MESSAGES.REQUIRED }).readonly(),
});

export const aoiSchema = z.object({
  aoiSelect: aoiOptionSchemaRequired,
  specSelect: optionSchemaRequired,
});

export type AOIFormValues = z.infer<typeof aoiSchema>;
export type CreateAOIFormValues = Omit<AOIFormValues, 'id'>;
export type AoiActorRef = AnyActorRef;
export interface Context {
  loading: boolean;
  requestProvider: (actorRef: AnyActorRef) => void;
  pagination: {
    page: number;
    pageSize: number;
    total: number;
  };
  aois: AreaOfInterest[];
  idToDelete: number;
  providerId: number;
  providerCategoryId: number;
  AOISpecialtyOptions: OptionType[];
  AOIOptions: OptionType[];
  specialtiesId: string;
  formValues: AOIFormValues;
  modal: boolean;
  _aoiBuffer: AreaOfInterest | undefined;
  // [MCV-1207] Inline submit error rendered inside the form dialog instead of a
  // generic corner toast. Set on the $_SEND_AOI onError path; cleared on retry,
  // dialog open/close.
  submitError: string | undefined;
}

export interface AOIResponse {
  data: AreaOfInterest;
}

export interface AOIPaginatedResponse {
  data: {
    data: AreaOfInterest[];
    page: number;
    pageSize: number;
  };
}
export interface AOIsResponse {
  data: AreaOfInterest[];
}

export interface Scope {
  context: Context;
  event: Events;
  system: AppActorSystem;
}

export type AOISortOptions = 'aoiName' | 'category' | 'description' | 'specialtyName' | 'id';

export type AOISort = {
  sortBy: AOISortOptions;
  category: number;
  description: number;
  specialtyName: number;
  id: number;
  page: number;
  pageSize: number;
};
